import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Gamepad2, Coins, Trophy, Play, Star, Users,
  Zap, Crown, RefreshCw, ChevronRight, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { getImageUrl } from '../utils/imageUrl';

const GameZone = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agpBalance, setAgpBalance] = useState(0);
  const [selectedGame, setSelectedGame] = useState(null);
  const [showDifficultyModal, setShowDifficultyModal] = useState(false);
  const [startingGame, setStartingGame] = useState(false);

  useEffect(() => {
    fetchGames();
    if (user) {
      fetchAGPBalance();
    }
  }, [user]);

  const fetchGames = async () => {
    try {
      const res = await api.get('/games');
      setGames(res.data.games || []);
    } catch (error) {
      console.error('Failed to fetch games:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAGPBalance = async () => {
    try {
      const res = await api.get('/agp/balance');
      setAgpBalance(res.data.balance || 0);
    } catch (error) {
      console.error('Failed to fetch AGP balance:', error);
    }
  };

  const handlePlayClick = (game) => {
    if (!user) {
      toast({
        title: "Login Required",
        description: "Please login to play games",
        variant: "destructive"
      });
      navigate('/login');
      return;
    }
    setSelectedGame(game);
    setShowDifficultyModal(true);
  };

  const startGame = async (difficulty) => {
    if (!selectedGame) return;

    const config = getDifficultyConfig(difficulty);
    
    // Check balance for paid games
    if (config.entryFee > 0 && agpBalance < config.entryFee) {
      toast({
        title: "Insufficient AGP",
        description: `You need ${config.entryFee} AGP. Current balance: ${agpBalance} AGP`,
        variant: "destructive"
      });
      setShowDifficultyModal(false);
      navigate('/agp-wallet');
      return;
    }

    setStartingGame(true);
    try {
      const res = await api.post(`/games/${selectedGame.slug}/start`, {
        difficulty
      });

      toast({
        title: "Game Started!",
        description: res.data.message,
      });

      // Navigate to game player (for now, show alert)
      setShowDifficultyModal(false);
      
      // Store session in localStorage for the game
      localStorage.setItem('currentGameSession', JSON.stringify({
        sessionId: res.data.session_id,
        gameId: res.data.game_id,
        gameSlug: selectedGame.slug,
        difficulty,
        lives: res.data.lives,
        gameUrl: res.data.game_url
      }));

      // Navigate to game player
      navigate(`/games/${selectedGame.slug}/play`);
      
    } catch (error) {
      toast({
        title: "Failed to Start",
        description: error.response?.data?.detail || "Something went wrong",
        variant: "destructive"
      });
    } finally {
      setStartingGame(false);
    }
  };

  const getDifficultyConfig = (difficulty) => {
    switch (difficulty) {
      case 'basic':
        return { entryFee: 0, lives: 'Unlimited', reward: 'None (Practice)', color: 'gray' };
      case 'advance':
        return { entryFee: 20, lives: '3 Lives', reward: '1-10 AGP', color: 'blue' };
      case 'super':
        return { entryFee: 50, lives: '2 Lives', reward: '1-10 AGP', color: 'purple' };
      default:
        return { entryFee: 0, lives: 'Unlimited', reward: 'None', color: 'gray' };
    }
  };

  const getGameTypeIcon = (type) => {
    switch (type) {
      case 'chess': return '♟️';
      case 'platformer': return '🦘';
      case 'puzzle': return '🧩';
      case 'runner': return '🏃';
      default: return '🎮';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="game-zone-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-purple-600/20 via-pink-500/10 to-orange-500/20 py-8 sm:py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
            <div className="text-center md:text-left">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold flex items-center justify-center md:justify-start gap-2 sm:gap-3">
                <Gamepad2 className="w-8 h-8 sm:w-10 sm:h-10 text-purple-400" />
                Fun & Entertainment Zone
              </h1>
              <p className="text-muted-foreground mt-2 text-sm sm:text-base">
                Play games, win AGP rewards, and have fun!
              </p>
            </div>
            
            {user && (
              <Card className="bg-background/80 backdrop-blur border-yellow-500/30 w-full md:w-auto">
                <CardContent className="p-3 sm:p-4 flex items-center justify-between md:justify-start gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-yellow-500/20 rounded-full">
                    <Coins className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-muted-foreground">AGP Balance</p>
                    <p className="text-xl sm:text-2xl font-bold text-yellow-400" data-testid="agp-balance-header">
                      {agpBalance.toFixed(0)} AGP
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate('/agp-wallet')}
                  >
                    + Buy
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-6 sm:mt-8">
            <div className="bg-background/50 backdrop-blur rounded-lg p-3 sm:p-4 text-center">
              <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400 mx-auto mb-1 sm:mb-2" />
              <p className="text-xl sm:text-2xl font-bold">{games.length}</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground">Games</p>
            </div>
            <div className="bg-background/50 backdrop-blur rounded-lg p-3 sm:p-4 text-center">
              <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-green-400 mx-auto mb-1 sm:mb-2" />
              <p className="text-xl sm:text-2xl font-bold">1-10</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground">AGP Rewards</p>
            </div>
            <div className="bg-background/50 backdrop-blur rounded-lg p-3 sm:p-4 text-center">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400 mx-auto mb-1 sm:mb-2" />
              <p className="text-xl sm:text-2xl font-bold">Free</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground">Practice Mode</p>
            </div>
          </div>
        </div>
      </div>

      {/* Games Grid */}
      <div className="max-w-6xl mx-auto py-6 sm:py-8 px-3 sm:px-4">
        <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center gap-2">
          <Star className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
          Available Games
        </h2>

        {games.length === 0 ? (
          <div className="text-center py-12">
            <Gamepad2 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No games available yet</p>
            <p className="text-sm text-muted-foreground">Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {games.map((game) => (
              <Card 
                key={game.game_id} 
                className="overflow-hidden hover:border-primary/50 transition-colors group"
                data-testid={`game-card-${game.slug}`}
              >
                <div className="aspect-video bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center relative">
                  {game.thumbnail ? (
                    <img 
                      src={getImageUrl(game.thumbnail)} 
                      alt={game.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-5xl sm:text-6xl">{getGameTypeIcon(game.type)}</span>
                  )}
                  <div className="absolute top-2 right-2 bg-background/80 backdrop-blur px-2 py-1 rounded text-xs">
                    {game.play_count || 0} plays
                  </div>
                </div>
                
                <CardContent className="p-3 sm:p-4">
                  <h3 className="font-bold text-lg mb-1">{game.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {game.description}
                  </p>
                  
                  {/* Difficulty badges */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {game.difficulties?.includes('basic') && (
                      <span className="text-xs px-2 py-1 bg-gray-500/20 text-gray-400 rounded">
                        Free
                      </span>
                    )}
                    {game.difficulties?.includes('advance') && (
                      <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded">
                        20 AGP
                      </span>
                    )}
                    {game.difficulties?.includes('super') && (
                      <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 rounded">
                        50 AGP
                      </span>
                    )}
                  </div>

                  <Button 
                    className="w-full group-hover:bg-primary"
                    onClick={() => handlePlayClick(game)}
                    data-testid={`play-${game.slug}-btn`}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Play Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Difficulty Selection Modal */}
      {showDifficultyModal && selectedGame && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardContent className="p-6">
              <div className="text-center mb-6">
                <span className="text-4xl mb-2 block">{getGameTypeIcon(selectedGame.type)}</span>
                <h3 className="text-xl font-bold">{selectedGame.name}</h3>
                <p className="text-sm text-muted-foreground">Select Difficulty</p>
              </div>

              <div className="space-y-3">
                {/* Basic - Free */}
                {selectedGame.difficulties?.includes('basic') && (
                  <button
                    onClick={() => startGame('basic')}
                    disabled={startingGame}
                    className="w-full p-4 rounded-lg border border-gray-500/30 hover:border-gray-400 transition-colors text-left"
                    data-testid="difficulty-basic-btn"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold flex items-center gap-2">
                          <span className="text-gray-400">FREE</span> BASIC
                        </p>
                        <p className="text-sm text-muted-foreground">Unlimited Lives | No Rewards</p>
                        <p className="text-xs text-gray-400 mt-1">Practice Mode</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </button>
                )}

                {/* Advance - 20 AGP */}
                {selectedGame.difficulties?.includes('advance') && (
                  <button
                    onClick={() => startGame('advance')}
                    disabled={startingGame}
                    className="w-full p-4 rounded-lg border border-blue-500/30 hover:border-blue-400 bg-blue-500/5 transition-colors text-left"
                    data-testid="difficulty-advance-btn"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold flex items-center gap-2">
                          <Star className="w-4 h-4 text-blue-400" /> ADVANCE
                        </p>
                        <p className="text-sm text-muted-foreground">3 Lives | Win 1-10 AGP</p>
                        <p className="text-xs text-blue-400 mt-1">Entry: 20 AGP</p>
                      </div>
                      <div className="text-right">
                        <Coins className="w-5 h-5 text-yellow-400" />
                        <p className="text-xs text-muted-foreground">20</p>
                      </div>
                    </div>
                  </button>
                )}

                {/* Super - 50 AGP */}
                {selectedGame.difficulties?.includes('super') && (
                  <button
                    onClick={() => startGame('super')}
                    disabled={startingGame}
                    className="w-full p-4 rounded-lg border border-purple-500/30 hover:border-purple-400 bg-purple-500/5 transition-colors text-left"
                    data-testid="difficulty-super-btn"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold flex items-center gap-2">
                          <Crown className="w-4 h-4 text-purple-400" /> SUPER
                        </p>
                        <p className="text-sm text-muted-foreground">2 Lives | Win 1-10 AGP</p>
                        <p className="text-xs text-purple-400 mt-1">Entry: 50 AGP</p>
                      </div>
                      <div className="text-right">
                        <Coins className="w-5 h-5 text-yellow-400" />
                        <p className="text-xs text-muted-foreground">50</p>
                      </div>
                    </div>
                  </button>
                )}
              </div>

              <div className="mt-4 p-3 bg-muted rounded-lg text-center">
                <p className="text-sm text-muted-foreground">
                  Your Balance: <span className="text-yellow-400 font-bold">{agpBalance.toFixed(0)} AGP</span>
                </p>
              </div>

              <Button 
                variant="ghost" 
                className="w-full mt-4"
                onClick={() => setShowDifficultyModal(false)}
              >
                Cancel
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default GameZone;
